
import { useEffect, useRef } from 'react';
import { useInView, animate } from 'motion/react';

export default function CountUp({
  to,
  from = 0,
  direction = 'up',
  delay = 0,
  duration = 2,
  className = '',
  startWhen = true,
  separator = '',
  onStart,
  onEnd
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '0px' });

  const start = direction === 'down' ? to : from;
  const end = direction === 'down' ? from : to;

  const getDecimalPlaces = num => {
    const str = num.toString();

    if (str.includes('.')) {
      const decimals = str.split('.')[1];

      if (parseInt(decimals) !== 0) {
        return decimals.length;
      }
    }

    return 0;
  };

  const maxDecimals = Math.max(getDecimalPlaces(from), getDecimalPlaces(to));

  const format = value => {
    const hasDecimals = maxDecimals > 0;

    const options = {
      useGrouping: !!separator,
      minimumFractionDigits: hasDecimals ? maxDecimals : 0,
      maximumFractionDigits: hasDecimals ? maxDecimals : 0
    };

    const formatted = Intl.NumberFormat('en-US', options).format(value);

    return separator ? formatted.replace(/,/g, separator) : formatted;
  };

  // Paint the starting value immediately.
  useEffect(() => {
    if (ref.current) {
      ref.current.textContent = format(start);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, maxDecimals, separator]);

  // Deterministic tween: finishes exactly at `duration` with a smooth easeOut.
  // Writes to the DOM ref directly (via rAF), no React re-renders.
  useEffect(() => {
    if (!isInView || !startWhen) return;

    if (typeof onStart === 'function') onStart();

    const controls = animate(start, end, {
      duration,
      delay,
      ease: 'easeOut',
      onUpdate: latest => {
        if (ref.current) ref.current.textContent = format(latest);
      },
      onComplete: () => {
        if (typeof onEnd === 'function') onEnd();
      }
    });

    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInView, startWhen, start, end, delay, duration]);

  return <span className={className} ref={ref} />;
}
